// src/services/reminderService.js
import { collection, addDoc, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from './firebase';
import { formatDate } from '../utils/helpers';

/**
 * Check if a reminder was already sent for this game today
 * Now requires clubId to satisfy Firebase security rules
 */
const wasReminderAlreadySent = async (gameId, clubId, reminderType = 'game_reminder') => {
  try {
    // If no clubId, skip the check and allow sending
    if (!clubId) {
      console.log('⚠️ No clubId provided, skipping duplicate check');
      return false;
    }

    const notificationsRef = collection(db, 'notifications');

    // Check for reminders sent today for this game
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayISO = today.toISOString();

    // Include clubId in query to satisfy security rules
    const q = query(
      notificationsRef,
      where('clubId', '==', clubId),
      where('gameId', '==', gameId),
      where('type', '==', reminderType)
    );

    const snapshot = await getDocs(q);

    // Check if any reminder was sent today
    for (const docSnap of snapshot.docs) {
      const notification = docSnap.data();
      const createdAt = new Date(notification.createdAt);
      createdAt.setHours(0, 0, 0, 0);

      if (createdAt.toISOString() === todayISO) {
        return true; // Already sent today
      }
    }

    return false;
  } catch (error) {
    console.error('Error checking existing reminders:', error);
    return false; // On error, allow sending (fail-open)
  }
};

/**
 * Send game reminder to players in the HOME team only (your club's team)
 * Away team is typically the opponent and doesn't need reminders from your system
 */
export const sendGameReminder = async (gameId) => {
  try {
    console.log('🔵 Sending game reminder for:', gameId);

    // Get game details first to get clubId
    const gameRef = doc(db, 'games', gameId);
    const gameSnap = await getDoc(gameRef);

    if (!gameSnap.exists()) {
      console.log('❌ Game not found');
      return { success: false, error: 'Game not found' };
    }

    const game = gameSnap.data();
    const clubId = game.clubId;

    // Check if reminder was already sent today (now with clubId)
    const alreadySent = await wasReminderAlreadySent(gameId, clubId, 'game_reminder');
    if (alreadySent) {
      console.log('⭕️ Reminder already sent today for this game, skipping');
      return { success: true, count: 0, skipped: true, message: 'Reminder already sent today' };
    }

    // Only get HOME team - this is your club's team that needs reminders
    // Away team is the opponent (either external or another club team that will get their own reminders)
    const teamId = game.homeTeamId || game.teamId;

    if (!teamId) {
      console.log('❌ Game has no home team assigned');
      return { success: false, error: 'No home team assigned to game' };
    }

    // Get home team players
    const teamRef = doc(db, 'teams', teamId);
    const teamSnap = await getDoc(teamRef);

    if (!teamSnap.exists()) {
      console.log('❌ Team not found');
      return { success: false, error: 'Team not found' };
    }

    const teamData = teamSnap.data();
    const players = (teamData.players || []).map(player => ({
      id: player.userId || player.id,
      name: player.name
    }));

    if (players.length === 0) {
      console.log('❌ No players found in home team');
      return { success: false, error: 'No players found' };
    }

    // Check which players already have a reminder for this game today
    const notificationsRef = collection(db, 'notifications');
    const existingQuery = query(
      notificationsRef,
      where('clubId', '==', clubId),
      where('gameId', '==', gameId),
      where('type', '==', 'game_reminder')
    );
    const existingSnapshot = await getDocs(existingQuery);

    // Get user IDs that already received reminders today
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const alreadyNotifiedToday = new Set();

    existingSnapshot.docs.forEach(docSnap => {
      const notification = docSnap.data();
      const createdAt = new Date(notification.createdAt);
      createdAt.setHours(0, 0, 0, 0);

      if (createdAt.getTime() === today.getTime()) {
        alreadyNotifiedToday.add(notification.userId);
      }
    });

    // Filter to only players who haven't been notified today
    const playersToNotify = players.filter(p => !alreadyNotifiedToday.has(p.id));

    if (playersToNotify.length === 0) {
      console.log('⭕️ All players already notified today');
      return { success: true, count: 0, skipped: true, message: 'All players already notified' };
    }

    // Format opponent name
    let opponentName = game.opponent || game.awayTeam || '';
    if (game.awayTeamId && !opponentName) {
      // Try to get away team name
      const awayTeamRef = doc(db, 'teams', game.awayTeamId);
      const awayTeamSnap = await getDoc(awayTeamRef);
      if (awayTeamSnap.exists()) {
        opponentName = awayTeamSnap.data().name || '';
      }
    }

    // Format the game date nicely
    const formattedDate = formatDate(game.date, {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });

    // Create notification for each player who hasn't been notified
    const notificationPromises = playersToNotify.map(player =>
      addDoc(notificationsRef, {
        userId: player.id,
        type: 'game_reminder',
        gameId: gameId,
        gameName: game.name,
        gameDate: formattedDate,
        gameVenue: game.venue || '',
        opponent: opponentName,
        message: `🏏 Upcoming Game: ${game.name}`,
        read: false,
        createdAt: new Date().toISOString(),
        clubId: clubId,
        urgent: isGameSoon(game.date)
      })
    );

    await Promise.all(notificationPromises);
    console.log(`✅ Game reminders sent to ${playersToNotify.length} players`);

    return {
      success: true,
      count: playersToNotify.length,
      players: playersToNotify.map(p => p.name)
    };
  } catch (error) {
    console.error('❌ Error sending game reminders:', error);
    return { success: false, error };
  }
};

// Helper to check if game is within 48 hours
const isGameSoon = (dateString) => {
  const gameDate = new Date(dateString);
  const now = new Date();
  const hoursUntilGame = (gameDate - now) / (1000 * 60 * 60);
  return hoursUntilGame <= 48;
};

/**
 * Send availability reminder to players who haven't responded
 * Call this 2-3 days before game
 */
export const sendAvailabilityReminder = async (gameId) => {
  try {
    console.log('🔵 Sending availability reminders for game:', gameId);

    // Get game details first to get clubId
    const gameRef = doc(db, 'games', gameId);
    const gameSnap = await getDoc(gameRef);

    if (!gameSnap.exists()) {
      console.log('❌ Game not found');
      return { success: false, error: 'Game not found' };
    }

    const game = gameSnap.data();
    const clubId = game.clubId;
    const availability = game.availability || {};

    // Check if availability reminder was already sent today (now with clubId)
    const alreadySent = await wasReminderAlreadySent(gameId, clubId, 'availability_reminder');
    if (alreadySent) {
      console.log('⭕️ Availability reminder already sent today, skipping');
      return { success: true, count: 0, skipped: true, message: 'Reminder already sent today' };
    }

    // Get team ID - use homeTeamId for new games, teamId for old games
    const teamId = game.homeTeamId || game.teamId;

    if (!teamId) {
      console.log('❌ Game has no team assigned');
      return { success: false, error: 'Game has no team assigned' };
    }

    // Get all team players
    const teamRef = doc(db, 'teams', teamId);
    const teamSnap = await getDoc(teamRef);

    if (!teamSnap.exists()) {
      console.log('❌ Team not found');
      return { success: false, error: 'Team not found' };
    }

    const team = teamSnap.data();
    const allPlayers = team.players || [];

    // Find players who haven't provided availability
    const playersWithoutAvailability = allPlayers.filter(player => {
      const playerId = player.userId || player.id;
      return !availability[playerId]; // No availability set
    });

    if (playersWithoutAvailability.length === 0) {
      console.log('✅ All players have provided availability');
      return { success: true, count: 0, message: 'All players responded' };
    }

    // Check which players already have an availability reminder for this game today
    const notificationsRef = collection(db, 'notifications');
    const existingQuery = query(
      notificationsRef,
      where('clubId', '==', clubId),
      where('gameId', '==', gameId),
      where('type', '==', 'availability_reminder')
    );
    const existingSnapshot = await getDocs(existingQuery);

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const alreadyNotifiedToday = new Set();

    existingSnapshot.docs.forEach(docSnap => {
      const notification = docSnap.data();
      const createdAt = new Date(notification.createdAt);
      createdAt.setHours(0, 0, 0, 0);

      if (createdAt.getTime() === today.getTime()) {
        alreadyNotifiedToday.add(notification.userId);
      }
    });

    // Filter to players who need reminders and haven't been notified today
    const playersToNotify = playersWithoutAvailability.filter(player => {
      const playerId = player.userId || player.id;
      return !alreadyNotifiedToday.has(playerId);
    });

    if (playersToNotify.length === 0) {
      console.log('⭕️ All relevant players already notified today');
      return { success: true, count: 0, skipped: true, message: 'All players already notified' };
    }

    // Format the game date nicely
    const formattedDate = formatDate(game.date, {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });

    // Send notifications to players without availability who haven't been notified
    const notificationPromises = playersToNotify.map(player => {
      const playerId = player.userId || player.id;
      return addDoc(notificationsRef, {
        userId: playerId,
        type: 'availability_reminder',
        gameId: gameId,
        gameName: game.name,
        gameDate: formattedDate,
        gameVenue: game.venue || '',
        message: `⏰ Please confirm your availability`,
        read: false,
        createdAt: new Date().toISOString(),
        clubId: clubId,
        urgent: true
      });
    });

    await Promise.all(notificationPromises);
    console.log(`✅ Availability reminders sent to ${playersToNotify.length} players`);

    return {
      success: true,
      count: playersToNotify.length,
      players: playersToNotify.map(p => p.name)
    };
  } catch (error) {
    console.error('❌ Error sending availability reminders:', error);
    return { success: false, error };
  }
};

/**
 * Check if game is within the next 7 days and send reminders
 * This can be called by a scheduled function or manually by admin
 */
export const checkAndSendUpcomingGameReminders = async (clubId) => {
  try {
    console.log('🔵 Checking for upcoming games requiring reminders');

    // Get all games for the club
    const gamesRef = collection(db, 'games');
    const q = query(gamesRef, where('clubId', '==', clubId));
    const snapshot = await getDocs(q);

    const now = new Date();
    const reminders = [];
    const processedGameIds = new Set(); // Track processed games to avoid duplicates

    for (const gameDoc of snapshot.docs) {
      const game = { id: gameDoc.id, ...gameDoc.data() };

      // Skip if we've already processed this game (shouldn't happen, but safety check)
      if (processedGameIds.has(game.id)) {
        console.log(`⭕️ Skipping duplicate game: ${game.name}`);
        continue;
      }
      processedGameIds.add(game.id);

      const gameDate = new Date(game.date);
      const hoursUntilGame = (gameDate - now) / (1000 * 60 * 60);

      console.log(`🔍 Game "${game.name}" - Date: ${game.date}, Hours until game: ${hoursUntilGame.toFixed(1)}`);

      // Send reminder if game is within next 7 days (including today)
      if (hoursUntilGame >= -24 && hoursUntilGame <= 168) {
        console.log(`📤 Sending reminder for game: ${game.name}`);
        const result = await sendGameReminder(game.id);

        // Only add to reminders if we actually sent something (not skipped)
        if (result.count > 0) {
          reminders.push({
            gameId: game.id,
            gameName: game.name,
            type: 'game_reminder',
            ...result
          });
        } else if (result.skipped) {
          console.log(`⭕️ Skipped (already sent): ${game.name}`);
        }
      }

      // Send availability reminder if game is 2-7 days away
      if (hoursUntilGame > 48 && hoursUntilGame <= 168) {
        const availResult = await sendAvailabilityReminder(game.id);

        // Only add to reminders if we actually sent something
        if (availResult.count > 0) {
          reminders.push({
            gameId: game.id,
            gameName: game.name,
            type: 'availability_reminder',
            ...availResult
          });
        }
      }
    }

    console.log(`✅ Sent ${reminders.length} reminder batches`);
    return { success: true, reminders };
  } catch (error) {
    console.error('❌ Error checking upcoming games:', error);
    return { success: false, error };
  }
};

export default {
  sendGameReminder,
  sendAvailabilityReminder,
  checkAndSendUpcomingGameReminders
};